/**
 * ChalkboardPanel — replaces the 2D instructor portrait with a
 * blackboard-style canvas that reveals key concepts paragraph-by-paragraph
 * as the TTS narration progresses.
 *
 * How it works
 *   • The parent (<LiveClassroomPanel/>) computes paragraph boundaries
 *     from the lesson markdown and tracks which one is "active" via
 *     the Web Speech API word index (`wordRange[0]` ÷ total chars).
 *   • For each active paragraph we extract the first 6-12 word headline
 *     (already-bolded text from `**...**` or the first sentence), wrap
 *     it to fit, and animate it onto the chalkboard with a fake
 *     handwriting effect (CSS `@keyframes draw-in` on a clip-path).
 *   • Older lines stay visible — the inner container scrolls so the
 *     learner can review everything that's been written so far. The
 *     newest line is always auto-scrolled into view.
 *
 * Why this beats a video for the price
 *   • Real-time, in-language, in-sync with the user's reading speed.
 *   • Looks "live" because content appears as it's spoken.
 *   • Zero asset cost; uses CSS-only handwriting illusion.
 */
import { useEffect, useMemo, useRef, useState } from "react";

const MAX_WORDS_PER_LINE = 10;

function _extractHeadline(paragraph) {
  if (!paragraph) return "";
  // Prefer a **bold** phrase if present
  const bold = paragraph.match(/\*\*(.+?)\*\*/);
  if (bold) return _truncWords(bold[1], MAX_WORDS_PER_LINE);
  // Otherwise the first sentence, capped
  const firstSentence = paragraph.split(/[.!?]/)[0];
  return _truncWords(firstSentence, MAX_WORDS_PER_LINE);
}

function _truncWords(text, max) {
  const parts = String(text || "").trim().split(/\s+/).filter(Boolean);
  return parts.slice(0, max).join(" ");
}

export default function ChalkboardPanel({ text, speaking, wordRange, size = 180 }) {
  const paragraphs = useMemo(() => {
    const raw = text || "";
    // 1. Try paragraph-level split first (double-newline delimiter,
    //    courtesy of the paragraph-preserving stripMd in
    //    LiveClassroomPanel).
    const paras = raw
      .split(/\n\s*\n/)
      .map((p) => p.trim())
      .filter((p) => p.length > 25);
    if (paras.length >= 2) return paras;
    // 2. Fallback: chunk by sentences (~140 chars each) so the
    //    chalkboard still draws lines for legacy single-line text.
    //    Without this fallback, paths whose markdown was generated
    //    before the paragraph-preserving stripMd would never reveal
    //    anything on the board.
    const sentences = raw
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 12);
    if (sentences.length <= 1) return [];
    // Bucket sentences into ~140-char groups so we don't draw a new
    // line every 6 words — too noisy for the eye.
    const buckets = [];
    let cur = "";
    for (const s of sentences) {
      if ((cur + " " + s).trim().length > 140 && cur) {
        buckets.push(cur.trim());
        cur = s;
      } else {
        cur = (cur ? cur + " " : "") + s;
      }
    }
    if (cur) buckets.push(cur.trim());
    return buckets;
  }, [text]);

  // Map every paragraph to its starting char-index in the stripped text
  // so we can find the "active" paragraph from the current wordRange.
  const boundaries = useMemo(() => {
    const arr = [];
    let cursor = 0;
    const stripped = (text || "");
    for (const p of paragraphs) {
      const idx = stripped.indexOf(p, cursor);
      if (idx >= 0) {
        arr.push({ start: idx, end: idx + p.length, headline: _extractHeadline(p) });
        cursor = idx + p.length;
      }
    }
    return arr;
  }, [paragraphs, text]);

  const [revealed, setRevealed] = useState([]); // list of headline strings
  const lastIdxRef = useRef(-1);
  const speakingStartRef = useRef(null);

  // Boundary-event driven reveal (primary path). When the Web Speech
  // API fires a `boundary` event, we know the char-index of the
  // currently-spoken word in the FULL text — pick the matching
  // paragraph and reveal its headline.
  useEffect(() => {
    if (!speaking) {
      // Reset the board when narration stops
      setRevealed([]);
      lastIdxRef.current = -1;
      speakingStartRef.current = null;
      return;
    }
    if (speakingStartRef.current == null) {
      speakingStartRef.current = Date.now();
    }
    if (!wordRange || !boundaries.length) return;
    const charIdx = wordRange[0] ?? 0;
    let activeIdx = -1;
    for (let i = 0; i < boundaries.length; i++) {
      if (charIdx >= boundaries[i].start && charIdx <= boundaries[i].end) {
        activeIdx = i;
        break;
      }
    }
    if (activeIdx >= 0 && activeIdx > lastIdxRef.current) {
      const headline = boundaries[activeIdx].headline;
      if (headline) {
        setRevealed((prev) => [...prev, headline]);
      }
      lastIdxRef.current = activeIdx;
    }
  }, [wordRange, speaking, boundaries]);

  // Time-based fallback. Some browsers (older Safari, certain TTS
  // engines) don't fire reliable `boundary` events — in those cases
  // `wordRange` stays null and the primary effect never reveals a
  // headline. To keep the chalkboard useful even there, we also tick
  // a timer that reveals one paragraph every (estimated reading
  // time / paragraph count) milliseconds while speaking is true.
  // The primary effect (boundary-driven) still wins when it fires —
  // we just check `lastIdxRef` to avoid double-revealing the same
  // paragraph from both sources.
  useEffect(() => {
    if (!speaking || !boundaries.length) return undefined;
    // Reading speed baseline: ~180 wpm × the rate set in
    // useSpeechNarration (0.92). Each paragraph averages ~30 words.
    const totalWords = (text || "").split(/\s+/).length;
    const estimatedSeconds = Math.max(15, (totalWords / 180) * 60 / 0.92);
    const perParaMs = Math.max(2200, (estimatedSeconds * 1000) / boundaries.length);
    const id = setInterval(() => {
      // Only reveal via timer if boundary events aren't keeping up.
      const nextIdx = lastIdxRef.current + 1;
      if (nextIdx >= boundaries.length) return;
      const headline = boundaries[nextIdx].headline;
      if (!headline) return;
      setRevealed((prev) => [...prev, headline]);
      lastIdxRef.current = nextIdx;
    }, perParaMs);
    return () => clearInterval(id);
  }, [speaking, boundaries, text]);

  const scrollRef = useRef(null);
  // Auto-scroll to the newest chalk line so the learner always sees
  // what was just written, even if the board has filled up.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Defer to next frame so the newly-mounted line is in the DOM.
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [revealed.length]);

  const height = Math.round(size * 1.25);
  return (
    <div
      data-testid="chalkboard-panel"
      data-speaking={speaking ? "true" : "false"}
      className="relative rounded-2xl overflow-hidden border border-emerald-900/40 shadow-inner"
      style={{
        width: size,
        height,
        background:
          "radial-gradient(120% 80% at 30% 10%, #1d4533 0%, #0f2a20 55%, #07150f 100%)",
      }}
    >
      {/* subtle chalk-dust texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />
      {/* wooden frame top + bottom */}
      <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-b from-amber-950 to-amber-900/50 z-10" />
      <div className="absolute inset-x-0 bottom-0 h-1.5 bg-gradient-to-t from-amber-950 to-amber-900/50 z-10" />
      <div
        ref={scrollRef}
        className="absolute inset-0 px-3.5 pt-4 pb-5 overflow-y-auto chalkboard-scroll"
      >
        {revealed.length === 0 ? (
          <div className="flex items-center justify-center h-full text-center text-emerald-200/40 text-[10px] font-serif italic tracking-wide px-2">
            {speaking
              ? "Listening…"
              : "Tap Read aloud — I'll write the key ideas here as I speak."}
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            {revealed.map((line, i) => (
              <div
                key={`${i}-${line}`}
                data-testid={`chalkboard-line-${i}`}
                className="font-serif italic text-emerald-50/85 text-[11px] leading-snug whitespace-pre-line chalkboard-line"
              >
                {line}
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`
        .chalkboard-scroll::-webkit-scrollbar { width: 4px; }
        .chalkboard-scroll::-webkit-scrollbar-track { background: transparent; }
        .chalkboard-scroll::-webkit-scrollbar-thumb {
          background: rgba(110, 231, 183, 0.25);
          border-radius: 2px;
        }
        .chalkboard-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(110, 231, 183, 0.25) transparent;
        }
        .chalkboard-line {
          color: rgba(236, 253, 245, 0.88);
          text-shadow: 0 0 4px rgba(110, 231, 183, 0.18);
          animation: chalk-in 1.6s ease-out forwards;
          clip-path: inset(0 100% 0 0);
          opacity: 0;
        }
        @keyframes chalk-in {
          0%   { clip-path: inset(0 100% 0 0); opacity: 0; }
          12%  { opacity: 1; }
          100% { clip-path: inset(0 0 0 0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
